import {
    TodoGet,
    TodoKind,
    TodoRelationGet,
    TodoRelationState,
    Ulid,
} from "@kase/my-marathon-communication";
import { ReadonlySignal, computed } from "@preact/signals";
import { upsert } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createListController } from "../utils";
import { TodoItem, TodoRelation } from "./interfaces";

const viewRelation = (item: TodoRelationGet): TodoRelation => ({
    ...item,
    _id: item.id,
    id: item.id.toString(),
    kindId: item.kind_id.toString(),
    todoId: item.todo.toString(),
});

export const makeTodo = ({ connection, company }: ApiSliceParams) => {
    const { items: relations, listen: listenRelations } = createListController<
        TodoRelationGet,
        TodoRelation,
        { kind: TodoKind; id: string }
    >({
        connection,
        list: (con, { id }) =>
            con.todo.relation.items({
                kind_id: Ulid.fromString(id),
                company: company.value,
            }),
        getKey: ({ kind, id }) => `${kind.toString()}-${id}`,
        view: viewRelation,
        name: "todo-relation",
    });
    const {
        items: todos,
        listen: listenTodos,
        started: startedTodos,
    } = createListController<TodoGet, TodoItem, TodoKind>({
        connection,
        list: (con, kind) => con.todo.items({ kind }),
        getKey: (kind) => kind.toString(),
        view: (item, kind) => {
            const rel = computed(() =>
                relations.value.filter(
                    (r) => r.kind === kind && r.todo.equals(item.id),
                ),
            );
            const res = {
                ...item,
                _id: item.id,
                id: item.id.toString(),
                relations: rel,
                done: computed(() =>
                    rel.value.every(
                        (r) => r.state === TodoRelationState.Checked,
                    ),
                ),
            };

            return res;
        },
        name: "todo",
    });
    const projectStore: Record<string, ReadonlySignal<TodoItem[]>> = {};

    const loadRelations = async (ids: Readonly<Ulid>[]) => {
        const result: TodoRelation[] = [];

        for await (const item of connection.value.todo.relation.get({
            id: ids,
            etagid: [],
        })) {
            result.push(viewRelation(item));
        }

        relations.value = upsert(relations.peek().slice(), result, "id");
    };

    const dropRelations = (ids: Readonly<Ulid>[]) => {
        relations.value = relations
            .peek()
            .filter((r) => !ids.some((id) => r._id.equals(id)));
    };

    const byProject = (projectId: string) => {
        if (!(projectId in projectStore)) {
            projectStore[projectId] = computed(() =>
                todos.value.filter((i) =>
                    i.relations.value.some(
                        (r) =>
                            r.kind === TodoKind.Project &&
                            r.kindId === projectId,
                    ),
                ),
            );

            if (!startedTodos(TodoKind.Project)) {
                listenTodos(TodoKind.Project);
            }

            listenRelations({ kind: TodoKind.Project, id: projectId });
        }

        return projectStore[projectId];
    };

    const createForProject = async (
        name: string,
        projectId: Readonly<Ulid>,
    ) => {
        const id = await connection.value.todo.create({
            kind: TodoKind.Project,
            name,
        });

        const relationId = await connection.value.todo.relation.create({
            kind: TodoKind.Project,
            kind_id: projectId,
            state: TodoRelationState.Empty,
            todo: id,
            company: company.value,
        });

        await loadRelations([relationId]);
    };

    const rename = async (todoId: Readonly<Ulid>, name: string) => {
        const item = todos.peek().find((i) => i._id.equals(todoId));

        if (!item) {
            throw new Error("Todo item not found");
        }

        await connection.value.todo.update({
            id: todoId,
            etag: item.etag,
            name,
        });
    };

    const setChecked = async (todoId: Readonly<Ulid>, checked: boolean) => {
        const item = todos.peek().find((i) => i._id.equals(todoId));

        if (!item) {
            throw new Error("Todo item not found");
        }

        const rel = item.relations.peek();

        if (rel.length === 0) {
            throw new Error("Todo item has no relations");
        }

        const promises: Promise<unknown>[] = [];
        const con = connection.value;

        // assume that all relations should be updated
        //
        for (const r of rel) {
            promises.push(
                con.todo.relation.update({
                    id: r._id,
                    etag: r.etag,
                    state: checked
                        ? TodoRelationState.Checked
                        : TodoRelationState.Empty,
                }),
            );
        }

        await Promise.all(promises);
        await loadRelations(rel.map((r) => r._id));
    };

    const remove = async (todoId: Readonly<Ulid>) => {
        const item = todos.peek().find((i) => i._id.equals(todoId));

        if (!item) {
            throw new Error("Todo item not found");
        }

        const rel = item.relations.peek();

        if (rel.length === 0) {
            throw new Error("Todo item has no relations");
        }

        const promises: Promise<unknown>[] = [];
        const con = connection.value;

        // assume that all relations should be deleted
        //
        for (const r of rel) {
            promises.push(
                con.todo.relation.delete({
                    id: r._id,
                    etag: r.etag,
                }),
            );
        }

        await Promise.all(promises);
        dropRelations(rel.map((r) => r._id));

        await con.todo.delete({
            id: todoId,
            etag: item.etag,
        });
    };

    return { byProject, createForProject, rename, setChecked, remove };
};
